import React, { useEffect, useState } from 'react';
import { IconButton, Typography, Box, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import Chip from '@components/Atoms/Chip';
import ShowIf from '@components/Atoms/ShowIf';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { normalizeRumProxyHost } from '@utils/rumProxy';
import * as curriculumAPI from '../../apis/curriculum';

const ACCENT_COLOR = '#8B5700';
const ACCENT_HOVER_BG = alpha(ACCENT_COLOR, 0.08);
const ACCENT_BORDER_FAINT = alpha(ACCENT_COLOR, 0.3);

const SERVICE_CATEGORIES = {
  'Core Functionality': [
    {
      service: 'ChalkTalk Main Site',
      url: 'https://www.chalktalk.com',
      description: 'Main ChalkTalk website and platform access.',
      domain: '*.chalktalk.com',
    },
    {
      service: 'AWS Media Storage',
      url: 'https://s3.amazonaws.com/chalktalk-media/exams/assets/2014-08-19/2-2-8.jpg',
      description:
        'Test assets hosted on AWS S3. It is used for loading images and other media resources.',
      domain: '*.amazonaws.com/chalktalk-media/*',
    },
  ],
  'Chat Support': [
    {
      service: 'Intercom',
      url: 'https://api-iam.intercom.io/ping',
      description: 'This service is used for customer support chat functionality.',
      domain: '*.intercom.io',
    },
    {
      service: 'Intercom CDN',
      url: 'https://js.intercomcdn.com/frame.3d3f6b0e.js',
      description: 'CDN for Intercom chat widget resources.',
      domain: '*.intercomcdn.com',
    },
    {
      service: 'HubSpot',
      url: 'https://js.hs-scripts.com/8096472.js',
      description:
        'This service is used for loading HubSpot scripts. It is essential for ChalkTalk support functionality, including live chat.',
      domain: '*.hs-scripts.com',
    },
  ],
  'Monitoring and Performance': [
    {
      service: 'Sentry',
      url: 'https://o515125.ingest.sentry.io/api/',
      description:
        'This service is used for error tracking and monitoring. It is essential for identifying and resolving issues within the ChalkTalk platform.',
      domain: '*.ingest.sentry.io',
    },
    {
      service: 'DataDog',
      url: 'https://browser-intake-us5-datadoghq.com/api/v2/',
      description:
        'This service is used for performance monitoring and analytics. It is essential for tracking platform behavior from user interactions and performance metrics within the ChalkTalk platform.',
      domain: 'browser-intake-us5-datadoghq.com',
    },
  ],
  Other: [
    {
      service: 'WordPress',
      url: 'https://wp.com',
      description: 'WordPress services for content management and blog functionality.',
      domain: '*.wp.com',
    },
  ],
  'Content Display': [
    {
      service: 'Google Accounts',
      url: 'https://accounts.google.com/',
      description: 'This service is used for permissions for user-managed content',
      domain: 'accounts.google.com',
    },
    {
      service: 'Google Drive',
      url: 'https://drive.google.com/',
      description: 'This service is used for user access to lesson content',
      domain: 'drive.google.com',
    },
    {
      service: 'Google Docs',
      url: 'https://docs.google.com/',
      description:
        'This service is used for delivering Lesson slideshows and presentations.',
      domain: 'docs.google.com',
    },
    {
      service: 'Google APIs',
      url: 'https://fonts.googleapis.com/css?family=Roboto:400',
      description:
        'This service is used for Google APIs including fonts. It is essential for the ChalkTalk platform.',
      domain: '*.googleapis.com',
    },
    {
      service: 'Google User Content',
      url: 'https://lh3.googleusercontent.com/a/default-user',
      description: 'Google hosted images and other media resources.',
      domain: '*.googleusercontent.com',
    },
    {
      service: 'Google Analytics',
      url: 'https://www.google-analytics.com/analytics.js',
      description:
        'This service is used for loading Google Analytics scripts. It is essential for tracking user interactions and analytics within the ChalkTalk platform.',
      domain: '*.google-analytics.com',
    },
    {
      service: 'Google Static Content',
      url: 'https://www.gstatic.com',
      description:
        'Google static content delivery network for fonts, scripts, and other resources.',
      domain: '*.gstatic.com',
    },
    {
      service: 'Learnosity',
      url: 'https://items.learnosity.com/?v2024.3.LTS',
      description:
        'This service is used for loading assessment items and resources. It is essential for features related to question display and interaction.',
      domain: '*.learnosity.com',
    },
    {
      service: 'Bootstrap CDN',
      url: 'https://maxcdn.bootstrapcdn.com/bootstrap/4.0.0/css/bootstrap.min.css',
      description: 'Bootstrap CSS framework delivered via CDN.',
      domain: '*.bootstrapcdn.com',
    },
    {
      service: 'Cloudflare CDN',
      url: 'https://cdnjs.cloudflare.com/ajax/libs/jquery/3.6.0/jquery.min.js',
      description: 'Cloudflare CDN service for JavaScript libraries.',
      domain: '*.cloudflare.com',
    },
    {
      service: 'JSDeliver CDN',
      url: 'https://cdn.jsdelivr.net/npm/mathjax@3.0.1/es5/tex-mml-chtml.js',
      description: 'JSDelivr CDN service for JavaScript libraries.',
      domain: 'cdn.jsdelivr.net',
    },
  ],
  'Content Display - Situational': [
    {
      service: 'Codecogs',
      url: 'https://latex.codecogs.com/',
      description: 'LaTeX math rendering service. It is used for loading math images.',
      domain: '*.codecogs.com',
    },
    {
      service: 'GeoGebra',
      url: 'https://www.geogebra.org',
      description:
        'This service is used for embedding and displaying interactive math resources for study halls.',
      domain: '*.geogebra.org',
    },
    {
      service: 'YouTube',
      url: 'https://www.youtube.com',
      description:
        'This service is used for embedding and displaying lesson video content.',
      domain: '*.youtube.com',
    },
  ],
};

// The RUM relay host comes from DD_RUM_PROXY_HOST so this page checks the host the app actually uses
const getRumProxyServices = () => {
  const host = normalizeRumProxyHost(process.env.DD_RUM_PROXY_HOST);
  return host
    ? [
        {
          service: 'ChalkTalk Edge',
          url: `https://${host}/api/v2/rum`,
          description: 'First-party relay for ChalkTalk usage and performance data. Required for usage reporting.',
          domain: host,
        },
      ]
    : [];
};

const ServiceAccordion = ({ service, url, description, domain, handleResult }) => {
  const SERVICE_STATUS = {
    LOADING: 'LOADING',
    SUCCESS: 'SUCCESS',
    FAILED: 'FAILED',
  };

  const [testStatus, setTestStatus] = useState(SERVICE_STATUS.LOADING);
  const [errorMessage, setErrorMessage] = useState(null);
  const [pingCount, setPingCount] = useState(0);

  const ping = (url, timeout = 6000) =>
    new Promise((resolve, reject) => {
      const controller = new AbortController();
      const { signal } = controller;

      const timeoutId = setTimeout(() => {
        controller.abort();
        reject(new Error('Request timed out'));
      }, timeout);

      fetch(url, { mode: 'no-cors', signal })
        .then(() => {
          clearTimeout(timeoutId);
          resolve(true);
        })
        .catch((error) => {
          clearTimeout(timeoutId);
          if (error.name === 'AbortError') {
            reject(new Error('Request was aborted'));
          } else {
            reject(new Error(error.message));
          }
        });
    });

  const getStatusColor = (status) => {
    switch (status) {
      case SERVICE_STATUS.SUCCESS:
        return 'success';
      case SERVICE_STATUS.FAILED:
        return 'error';
      default:
        return 'info';
    }
  };

  useEffect(() => {
    ping(url)
      .then(() => {
        setTestStatus(SERVICE_STATUS.SUCCESS);
        handleResult?.(domain, SERVICE_STATUS.SUCCESS);
      })
      .catch((error) => {
        setTestStatus(SERVICE_STATUS.FAILED);
        setErrorMessage(`${error}`);
        handleResult?.(domain, SERVICE_STATUS.FAILED);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, url, pingCount, SERVICE_STATUS.SUCCESS, SERVICE_STATUS.FAILED]);

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${service}-content`}
        id={`${service}-header`}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Typography>{`${service} Service`}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={(event) => {
                event.stopPropagation();
                setTestStatus(SERVICE_STATUS.LOADING);
                setErrorMessage(null);
                setPingCount((prevCount) => prevCount + 1);
              }}
              title={`Redo ${service} Test`}
              aria-label={`Redo ${service} test`}
            >
              <RestartAltIcon aria-hidden="true" />
            </IconButton>
            <Chip
              label={testStatus}
              variant="contained"
              color={getStatusColor(testStatus)}
            />
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Typography variant="h4">{service}</Typography>
        <Typography variant="body1" color="textSecondary">
          {description}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ overflowWrap: 'break-word', fontWeight: 'bold' }}
        >
          Domain: {domain}
        </Typography>
        <ShowIf If={testStatus === SERVICE_STATUS.FAILED}>
          <Typography variant="h6" color="error">
            {errorMessage || 'Service endpoint unreachable.'}
          </Typography>
          <Typography variant="h6" color="error">
            {`The app is unable to access the domain ${domain}. Please include this information in your support request.`}
          </Typography>
        </ShowIf>
      </AccordionDetails>
    </Accordion>
  );
};

const CategorySection = ({ categoryName, services, handleResult }) => {
  const isOptional = categoryName.includes('Optional');

  return (
    <Box sx={{ mb: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          textAlign: 'center',
          mb: 2,
          color: isOptional ? 'text.secondary' : 'text.primary',
        }}
      >
        {categoryName}
      </Typography>
      {isOptional && (
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ textAlign: 'center', mb: 2, fontStyle: 'italic' }}
        >
          These services are optional and may not be required for all features.
        </Typography>
      )}
      {services.map((service) => (
        <ServiceAccordion key={service.url} {...service} handleResult={handleResult} />
      ))}
    </Box>
  );
};

const ExternalServiceChecker = () => {
  const [failedServices, setFailedServices] = useState([]);
  const serviceCategories = {
    ...SERVICE_CATEGORIES,
    'Monitoring and Performance': [...SERVICE_CATEGORIES['Monitoring and Performance'], ...getRumProxyServices()],
  };

  const openSystemRequirements = () => {
    curriculumAPI
      .getSystemRequirements()
      .then(({ response }) => window.open(response.data.url, '_blank'));
  };

  const handleResult = (serviceDomain, status) => {
    if (status === 'SUCCESS') {
      setFailedServices((prev) => prev.filter((domain) => domain !== serviceDomain));
    } else if (status === 'FAILED' && !failedServices.includes(serviceDomain)) {
      setFailedServices((prev) => [...prev, serviceDomain]);
    }
  };

  // Split categories into two columns for better space utilization
  const leftColumnCategories = [
    'Core Functionality',
    'Chat Support',
    'Monitoring and Performance',
    'Other',
  ];

  const rightColumnCategories = ['Content Display', 'Content Display - Situational'];

  return (
    <Box
      sx={{
        height: '100vh',
        overflowY: 'auto',
        px: 20,
        py: 5,
      }}
    >
      {/* Header Section with Logo and Back Button */}
      <Box
        sx={{
          position: 'relative',
          textAlign: 'center',
          mb: 3,
          pt: 1,
        }}
      >
        {/* Back to Home Button */}
        <Button
          href="/"
          startIcon={<HomeIcon />}
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            color: ACCENT_COLOR,
            borderColor: ACCENT_COLOR,
            '&:hover': {
              backgroundColor: ACCENT_HOVER_BG,
              borderColor: ACCENT_COLOR,
            },
          }}
          variant="outlined"
          size="small"
        >
          Back to Home
        </Button>

        {/* Logo */}
        <Box
          sx={{
            display: 'inline-block',
            mb: 1.5,
            animation: 'fadeIn 0.6s ease-in',
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(-20px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          <img
            src="/images/icon-v2/logo-chalktalk.png"
            alt="ChalkTalk Logo"
            style={{
              height: '50px',
              width: 'auto',
              filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
            }}
          />
        </Box>

        {/* Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            color: '#2b1a08',
            mb: 1,
            fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
            textShadow: '0 2px 4px rgba(0,0,0,0.05)',
          }}
        >
          ChalkTalk Service Checker
        </Typography>

        {/* Subtitle */}
        <Box
          sx={{
            mx: 'auto',
            px: 2,
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#666',
              lineHeight: 1.5,
              fontSize: { xs: '0.813rem', sm: '0.875rem' },
            }}
          >
            Use this page to check if your network has access to services required by
            ChalkTalk.
            <br />
            If any of these services are unreachable, please send the details below and
            our{' '}
            <a
              onClick={openSystemRequirements}
              style={{
                color: ACCENT_COLOR,
                textDecoration: 'none',
                fontWeight: 600,
                borderBottom: `1px solid ${ACCENT_BORDER_FAINT}`,
                cursor: 'pointer',
              }}
            >
              system requirements document
            </a>{' '}
            to your IT team and/or{' '}
            <a
              href="mailto:support@chalktalk.com"
              style={{
                color: ACCENT_COLOR,
                textDecoration: 'none',
                fontWeight: 600,
                borderBottom: `1px solid ${ACCENT_BORDER_FAINT}`,
              }}
            >
              support@chalktalk.com
            </a>
            .
          </Typography>
        </Box>
      </Box>

      <ShowIf If={failedServices.length > 0}>
        <Box
          sx={{
            mb: 4,
            p: 3,
            backgroundColor: '#ffebee',
            borderRadius: 2,
            border: '2px solid #ef5350',
            boxShadow: '0 4px 6px rgba(239, 83, 80, 0.1)',
            width: 'fit-content',
            mx: 'auto',
          }}
        >
          <Typography
            variant="h6"
            color="error"
            sx={{
              textAlign: 'center',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
            }}
          >
            <span>⚠️</span>
            The following domains are unreachable:
          </Typography>
          {failedServices.map((serviceDomain) => (
            <Typography
              key={serviceDomain}
              variant="body1"
              color="error"
              sx={{
                textAlign: 'center',
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              }}
            >
              {serviceDomain}
            </Typography>
          ))}
        </Box>
      </ShowIf>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 4,
          alignItems: 'flex-start',
        }}
      >
        {/* Left Column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {leftColumnCategories.map((categoryName) => (
            <CategorySection
              key={categoryName}
              categoryName={categoryName}
              services={serviceCategories[categoryName]}
              handleResult={handleResult}
            />
          ))}
        </Box>

        {/* Right Column */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {rightColumnCategories.map((categoryName) => (
            <CategorySection
              key={categoryName}
              categoryName={categoryName}
              services={serviceCategories[categoryName]}
              handleResult={handleResult}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ExternalServiceChecker;
